import {Advertisement} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/advertisement.types';
import {lazy} from '@aboutyou/lazy-imports';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions} from '@aboutyou/tracking';
import {
  ImageTile,
  InfoIconWithLink,
  Slot,
  desktopAndUp,
  useGetAriaLabelFromUrl,
  useInViewEffect,
} from '@aboutyou/ui-components';
import React from 'react';
import styled, {css} from 'styled-components';
import {convertAdvertisementToTile} from '../../../shared-deprecated/convertAdvertisementToTile';
import {useABTestAYOBasBanner} from '../../AyOutletAbTest/useABTestAYOBasBanner';

const TrackBASMonitoring = lazy(() =>
  import('@aboutyou/tracking/src/trackerComponents/TrackBASMonitoring').then(
    mod => ({
      default: mod.TrackBASMonitoring,
    }),
  ),
);

type Props = {
  className?: string;
  tile: Advertisement;
  gridColumns: number | undefined;
  streamPosition: number;
  inlineBASPlacements?: boolean;
  basInfoIconLink?: string;
};

export const StreamAdvertisementNormalized: React.FC<Props> = ({
  className,
  tile,
  streamPosition,
  gridColumns,
  inlineBASPlacements,
  basInfoIconLink,
}) => {
  const {shouldHideBasBanner, activateABTestAYOBasBanner, isAYOBasBanner} =
    useABTestAYOBasBanner();
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();

  const {ref} = useInViewEffect<HTMLDivElement>(
    inView => {
      if (inView && isAYOBasBanner(targetUrl)) {
        activateABTestAYOBasBanner();
      }
    },
    {triggerOnce: true},
  );

  const advertisementTile = convertAdvertisementToTile(tile);

  if (advertisementTile === null) {
    return (
      <TrackBASMonitoring
        basPromotionSource={TrackingPromotionCreative.BasCategoryTeaser}
      />
    );
  }

  const {image, targetUrl, promotionTracking, id} = advertisementTile;

  if (isAYOBasBanner(targetUrl) && shouldHideBasBanner) {
    return (
      // this slot is used to activate the AYOBasBanner A/B test
      <Slot position={streamPosition} ref={ref} Comp="li">
        <TrackBASMonitoring
          basPromotionSource={TrackingPromotionCreative.BasCategoryTeaser}
        />
      </Slot>
    );
  }

  const renderedImageTile = (
    <StyledTrackPromotions
      className={className}
      promotion={{
        advertisement: {
          id,
          path: targetUrl || '',
          name: promotionTracking!.name,
          promotionId: promotionTracking!.id,
        },
        source: TrackingPromotionCreative.BasCategoryTeaser,
      }}
      streamPosition={0}
      threshold={inlineBASPlacements ? 0.4 : 0.5}
      triggerOnce
      Comp={inlineBASPlacements ? 'li' : undefined}
    >
      <ImageTile
        ref={ref}
        description={promotionTracking!.name}
        imageUrl={image.url}
        targetUrl={targetUrl}
        ratio={inlineBASPlacements ? '3:4' : '5:4'}
        disableFixedRatio={inlineBASPlacements}
        importance="low"
        loading="lazy"
      />
      {basInfoIconLink && (
        <StyledInfoIconWithLink
          to={basInfoIconLink}
          aria-label={getAriaLabelFromUrl(basInfoIconLink)}
        />
      )}
    </StyledTrackPromotions>
  );

  if (inlineBASPlacements) {
    return renderedImageTile;
  }

  return (
    <Slot Comp="li" columns={gridColumns} position={streamPosition}>
      {renderedImageTile}
    </Slot>
  );
};

const StyledTrackPromotions = styled(TrackPromotions)`
  position: relative;
`;

const StyledInfoIconWithLink = styled(InfoIconWithLink)`
  && {
    top: 16px;

    ${desktopAndUp(css`
      right: 16px;
    `)}
  }
`;
